import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Shield, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">TB</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-white text-lg leading-none">TESTBOOK</span>
                <span className="text-xs text-gray-400">LTD</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Fast, secure, and reliable driving test booking service for all UK test centres.
            </p>
            <div className="flex items-center text-sm text-gray-400">
              <Shield className="w-4 h-4 mr-2" />
              <span>Registered Company</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
              </li>
            </ul>
          </div>

          {/* Test Types */}
          <div>
            <h3 className="text-white font-semibold mb-4">Test Types</h3>
            <ul className="space-y-2 text-sm">
              <li>Car (Category B)</li>
              <li>Motorcycle</li>
              <li>Lorry</li>
              <li>Bus</li>
              <li>ADI (Driving Instructor)</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Information</h3>
            <ul className="space-y-3 text-sm mb-6">
              <li className="flex items-start">
                <Mail className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>info@testbook.co.uk</span>
              </li>
              <li className="flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>
                  20 Wenlock Road<br />
                  London, England<br />
                  N1 7GU
                </span>
              </li>
            </ul>
            
            {/* Social Media */}
            <div>
              <h3 className="text-white font-semibold mb-3">Follow Us</h3>
              <div className="flex gap-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-400 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400 text-center md:text-left">
              <p className="mb-1">© 2025 TESTBOOK LTD. All rights reserved.</p>
              <p>TESTBOOK LTD is a registered company in England and Wales.</p>
              <p className="mt-1">Company Number: <span className="font-semibold">16836503</span></p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-xs text-gray-400">
                <Shield className="w-4 h-4 mr-1" />
                <span>Secure Payment</span>
              </div>
              <div className="text-xs text-gray-400 border-l border-gray-700 pl-4">
                🇬🇧 UK Company
              </div>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <p className="text-xs text-gray-500 text-center leading-relaxed max-w-4xl mx-auto">
              <strong className="text-gray-400">Important Notice:</strong> TESTBOOK LTD is an independent third-party service provider. 
              We are not affiliated with, endorsed by, or connected to the Driver and Vehicle Standards Agency (DVSA) 
              or any UK government agency. You can book your driving test directly at{' '}
              <a 
                href="https://www.gov.uk/book-driving-test" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                www.gov.uk/book-driving-test
              </a>
              . Our service provides booking assistance for a fee.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
